import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useLanguage, Language } from '../../contexts/LanguageContext';
import { HiX, HiGlobeAlt, HiCog } from 'react-icons/hi';

interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  padding: 20px;
  box-sizing: border-box;
  padding: 20px;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  background-color: ${({theme}) => theme.background};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  align-self: center;
  margin: 0 auto;
  margin: 0 auto;
  align-self: center;

  /* Responsividade para tablets */
  @media (max-width: 1024px) {
    width: 85%;
    max-width: 450px;
  }

  /* Responsividade para mobile */
  @media (max-width: 768px) {
    width: 95%;
    max-width: none;
    border-radius: 8px;
    max-height: 95vh;
  }

  /* Mobile pequeno */
  @media (max-width: 480px) {
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid ${({theme}) => theme.border};
  
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: ${({theme}) => theme.text};
  }

  /* Responsividade para mobile */
  @media (max-width: 768px) {
    padding: 20px;
    
    h2 {
      font-size: 18px;
    }
  }

  /* Mobile pequeno */
  @media (max-width: 480px) {
    padding: 16px;
    
    h2 {
      font-size: 16px;
    }
  }
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${({theme}) => theme.textSecondary};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({theme}) => theme.backgroundSecondary};
    color: ${({theme}) => theme.text};
  }
`;

const ModalBody = styled.div`
  padding: 24px;

  /* Responsividade para mobile */
  @media (max-width: 768px) {
    padding: 20px;
  }

  /* Mobile pequeno */
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const PreferenceSection = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${({theme}) => theme.text};
  }
  
  svg {
    color: ${({theme}) => theme.textSecondary};
  }
`;

const LanguageOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LanguageOption = styled.button.attrs({ type: 'button' })<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid ${props => props.$isActive ? props.theme.buttonBackground : props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.$isActive ? props.theme.backgroundSecondary : 'transparent'};
  color: ${({theme}) => theme.text};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({theme}) => theme.backgroundSecondary};
    border-color: ${({theme}) => theme.buttonBackground};
  }
  
  .language-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    
    .language-name {
      font-weight: 600;
      margin-bottom: 4px;
    }
    
    .language-native {
      font-size: 12px;
      color: ${({theme}) => theme.textSecondary};
    }
  }
  
  .check-mark {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${props => props.$isActive ? props.theme.buttonBackground : 'transparent'};
    border: 2px solid ${props => props.$isActive ? props.theme.buttonBackground : props.theme.border};
    display: flex;
    align-items: center;
    justify-content: center;
    
    &::after {
      content: '✓';
      color: ${({ theme }) => theme.text};
      font-size: 12px;
      font-weight: bold;
      opacity: ${props => props.$isActive ? 1 : 0};
    }
  }
`;

const PreferencesModal: React.FC<PreferencesModalProps> = ({ isOpen, onClose }) => {
  const { language, setLanguage, t } = useLanguage();
  const [selectedModel, setSelectedModel] = useState<
    'gpt-4' | 'gpt_4_1' | 'gpt_5' | 'openai_5_1' | 'gemini_3' | 'gemini_2_5'
  >('gpt-4');

  // Carregar modelo salvo do localStorage
  useEffect(() => {
    const savedModel = localStorage.getItem('selectedGptModel') as
      | 'gpt-4'
      | 'gpt_4_1'
      | 'gpt_5'
      | 'openai_5_1'
      | 'gemini_3'
      | 'gemini_2_5';
    if (savedModel) {
      setSelectedModel(savedModel);
    }
  }, []);

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
  };

  const handleModelChange = (
    model: 'gpt-4' | 'gpt_4_1' | 'gpt_5' | 'openai_5_1' | 'gemini_3' | 'gemini_2_5'
  ) => {
    setSelectedModel(model);
    localStorage.setItem('selectedGptModel', model);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  if (!portalTarget) {
    return null;
  }

  return createPortal(
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <h2>{t && typeof t === 'function' ? t('header.preferences') : 'Preferências'}</h2>
          <CloseButton onClick={onClose}>
            {HiX({ size: 18 })}
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <PreferenceSection>
            <SectionHeader>
              {HiGlobeAlt({ size: 20 })}
              <h3>{t && typeof t === 'function' ? t('preferences.language') : 'Idioma'}</h3>
            </SectionHeader>
            
            <LanguageOptions>
              <LanguageOption
                $isActive={language === 'pt'}
                onClick={() => handleLanguageChange('pt')}
              >
                <div className="language-info">
                  <div className="language-name">{t && typeof t === 'function' ? t('preferences.portuguese') : 'Português'}</div>
                  <div className="language-native">Português (Brasil)</div>
                </div>
                <div className="check-mark" />
              </LanguageOption>
              
              <LanguageOption
                $isActive={language === 'en'}
                onClick={() => handleLanguageChange('en')}
              >
                <div className="language-info">
                  <div className="language-name">{t && typeof t === 'function' ? t('preferences.english') : 'English'}</div>
                  <div className="language-native">English (US)</div>
                </div>
                <div className="check-mark" />
              </LanguageOption>
            </LanguageOptions>
          </PreferenceSection>

          <PreferenceSection>
            <SectionHeader>
              {HiCog({ size: 20 })}
              <h3>Modelo GPT</h3>
            </SectionHeader>
            
            <LanguageOptions>
              <LanguageOption
                $isActive={selectedModel === 'gpt-4'}
                onClick={() => handleModelChange('gpt-4')}
              >
                <div className="language-info">
                  <div className="language-name">GPT-4</div>
                  <div className="language-native">Modelo padrão</div>
                </div>
                <div className="check-mark" />
              </LanguageOption>
              
              <LanguageOption
                $isActive={selectedModel === 'gpt_4_1'}
                onClick={() => handleModelChange('gpt_4_1')}
              >
                <div className="language-info">
                  <div className="language-name">GPT-4.1</div>
                  <div className="language-native">Versão aprimorada</div>
                </div>
                <div className="check-mark" />
              </LanguageOption>
              
              <LanguageOption
                $isActive={selectedModel === 'gpt_5'}
                onClick={() => handleModelChange('gpt_5')}
              >
                <div className="language-info">
                  <div className="language-name">GPT-5</div>
                  <div className="language-native">Última geração</div>
                </div>
                <div className="check-mark" />
              </LanguageOption>
              
              <LanguageOption
                $isActive={selectedModel === 'openai_5_1'}
                onClick={() => handleModelChange('openai_5_1')}
              >
                <div className="language-info">
                  <div className="language-name">OpenAI 5.1</div>
                  <div className="language-native">OpenAI 5.1</div>
                </div>
                <div className="check-mark" />
              </LanguageOption>
              
              <LanguageOption
                $isActive={selectedModel === 'gemini_3'}
                onClick={() => handleModelChange('gemini_3')}
              >
                <div className="language-info">
                  <div className="language-name">Gemini 3</div>
                  <div className="language-native">Gemini 3</div>
                </div>
                <div className="check-mark" />
              </LanguageOption>
              
              <LanguageOption
                $isActive={selectedModel === 'gemini_2_5'}
                onClick={() => handleModelChange('gemini_2_5')}
              >
                <div className="language-info">
                  <div className="language-name">Gemini 2.5</div>
                  <div className="language-native">Gemini 2.5</div>
                </div>
                <div className="check-mark" />
              </LanguageOption>
            </LanguageOptions>
          </PreferenceSection>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>,
    portalTarget
  );
};

export default PreferencesModal; 